/**
 * GlobalMetricsEditModal.tsx — edits ONE metric (add or edit).
 *
 * "+ metric" opens this modal in create mode (blank form); double-clicking a
 * metric card opens it in edit mode (pre-filled). Same fields as the old
 * inline expander — label, key (ID), unit, direction, scope, formula (CEL,
 * textarea), group filter — one metric at a time. The formula field is a
 * textarea (ExprField multiLine).
 *
 * The host owns the metric + the mutation callbacks; this modal is a pure
 * editor over a single MetricSpec.
 */
import { useMemo, useState } from 'react';
import { Check, Trash } from '@phosphor-icons/react';

import type { MetricSpec, ScenarioBundle } from '../engine/types';
import { Modal } from './Modal';
import { ExprField } from './ExprField';

interface GlobalMetricsEditModalProps {
  /** The metric being edited. null = creating a new one. */
  metric: MetricSpec | null;
  bundle: ScenarioBundle;
  /** True when `metric.key` is taken by another metric (edit mode only). */
  keyTaken?: (key: string) => boolean;
  onClose: () => void;
  /** Save the edited metric. In create mode the host mints/inserts it. */
  onSave: (m: MetricSpec) => void;
  /** Edit mode only — remove the metric being edited. */
  onDelete?: () => void;
}

export function GlobalMetricsEditModal({
  metric,
  bundle,
  keyTaken,
  onClose,
  onSave,
  onDelete,
}: GlobalMetricsEditModalProps): JSX.Element {
  const creating = metric == null;
  const [draft, setDraft] = useState<MetricSpec>(
    () =>
      metric ?? {
        key: '',
        label: '',
        direction: 'up',
        expr: '0',
        nodeId: null,
      },
  );
  // Name → key auto-derivation in create mode (until the user edits the ID).
  const [idTouched, setIdTouched] = useState(!creating);
  const patch = (p: Partial<MetricSpec>) => setDraft((d) => ({ ...d, ...p }));

  const dummyScope = useMemo(() => buildDummyScope(bundle), [bundle]);
  const scope = draft.nodeId
    ? { ...dummyScope, self: dummyScope.nodes[draft.nodeId] ?? {} }
    : dummyScope;

  // Derive key from label (create mode, until ID is hand-edited).
  const derivedKey = useMemo(() => slugify(draft.label) || 'metric', [draft.label]);
  const effectiveKey = idTouched ? draft.key : derivedKey;
  const taken = !!keyTaken?.(effectiveKey);
  const keyValid = effectiveKey.length > 0 && !taken;
  const canSave = draft.label.trim().length > 0 && keyValid;

  const save = () => {
    if (!canSave) return;
    onSave({ ...draft, key: effectiveKey });
  };

  return (
    <Modal title={creating ? 'Add metric' : 'Edit metric'} onClose={onClose}>
      <section className="modal-section">
        {/* Name + ID pattern: name input with trailing "ID" addon. In create
            mode the ID auto-derives from the name until hand-edited. */}
        <div className="node-name-group">
          <input
            type="text"
            className="text-input node-name-input"
            placeholder="Metric name"
            value={draft.label}
            autoFocus
            onChange={(e) => patch({ label: e.target.value })}
            onKeyDown={(e) => { if (e.key === 'Enter') save(); }}
          />
          <button
            type="button"
            className="node-id-addon"
            title="ID"
            onClick={() => { setIdTouched(true); patch({ key: effectiveKey }); }}
          >
            ID
          </button>
        </div>
        {/* ID editor (always visible so the user can read/change the key). */}
        <div className="node-id-edit">
          <span className="node-id-edit-label">ID</span>
          <input
            type="text"
            className={`text-input node-id-input${taken ? ' invalid' : ''}`}
            value={effectiveKey}
            onChange={(e) => { setIdTouched(true); patch({ key: e.target.value }); }}
            onKeyDown={(e) => { if (e.key === 'Enter') save(); }}
          />
          {taken && <span className="id-hint">already in use</span>}
        </div>

        <div className="metric-edit-fields">
          <label className="metric-field">
            <span>Unit</span>
            <input
              type="text"
              className="text-input"
              placeholder="e.g. $, %, orders"
              value={draft.unit ?? ''}
              onChange={(e) => patch({ unit: e.target.value })}
            />
          </label>
          <label className="metric-field">
            <span>Direction</span>
            <select
              className="seg-select"
              value={draft.direction}
              onChange={(e) => patch({ direction: e.target.value as 'up' | 'down' })}
            >
              <option value="up">▲ up = good</option>
              <option value="down">▼ down = good</option>
            </select>
          </label>
          <label className="metric-field">
            <span>Scope</span>
            <select
              className="seg-select"
              value={draft.nodeId ?? ''}
              onChange={(e) => patch({ nodeId: e.target.value || null })}
            >
              <option value="">global (all nodes)</option>
              {bundle.graph.nodes.map((n) => (
                <option key={n.id} value={n.id}>per-node: {n.label}</option>
              ))}
            </select>
          </label>
          <label className="metric-field metric-field-last">
            <span>group filter (optional)</span>
            <input
              type="text"
              className="text-input"
              value={draft.group ?? ''}
              onChange={(e) => patch({ group: e.target.value || undefined })}
            />
          </label>
          <ExprField
            label="Formula (CEL)"
            value={draft.expr}
            scope={scope}
            onChange={(expr) => patch({ expr })}
            placeholder={draft.nodeId ? 'e.g. self.stock > 10 ? self.inflow : 0' : 'e.g. nodes.bank.inflow + nodes.factory.stock'}
            multiLine
          />
        </div>

        <div className="modal-actions">
          {!creating && onDelete && (
            <button type="button" className="btn btn-danger btn-mini" onClick={onDelete}>
              <Trash size={14} /> Delete
            </button>
          )}
          <button type="button" className="btn btn-start btn-mini" onClick={save} disabled={!canSave}>
            <Check size={14} weight="bold" /> {creating ? 'Add' : 'Save'}
          </button>
        </div>
      </section>
    </Modal>
  );
}

function slugify(s: string): string {
  return s.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
}

function buildDummyScope(bundle: ScenarioBundle): Record<string, any> {
  const nodes: Record<string, any> = {};
  for (const n of bundle.graph.nodes) {
    const p: Record<string, number> = { inflow: 0, outflow: 0, stock: 0 };
    for (const param of n.params) p[param.key] = 0;
    for (const param of n.gate?.params ?? []) p[param.key] = 0;
    for (const param of n.source?.distParams ?? []) p[param.key] = 0;
    if (n.computedMetrics) {
      for (const cm of n.computedMetrics) p[cm.key] = 0;
    }
    nodes[n.id] = new Proxy(p, {
      has() { return true; },
      get(target, prop) { return target[prop as string] ?? 0; }
    });
  }
  const edges: Record<string, any> = {};
  for (const e of bundle.graph.edges) {
    const ep: Record<string, number> = { flow: 0 };
    for (const param of e.params) ep[param.key] = 0;
    edges[e.id] = ep;
  }
  return { nodes, edges };
}
